package com.elan.edubridge.ui.platform

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.renderPage
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement

/**
 * 학원 멤버십과 독립적인 플랫폼 관리자 전용 셸이다. 스태프 [DashboardShell]은
 * active academy가 필요하므로 SUPER_ADMIN 콘솔에는 사용하지 않는다.
 */
internal object PlatformDashboardShell {
    private val scope = MainScope()
    private val accountRepository = SharedGraph.createAccountRepository()

    enum class Page(val path: String, val label: String, val iconId: String) {
        DASHBOARD("/app/admin", "대시보드", "icon-home"),
        ACADEMY_APPLICATIONS("/app/admin/applications", "학원 승인", "icon-academy"),
    }

    fun render(session: SessionState, activePage: Page, contentHtml: String) {
        val navigation = Page.entries.joinToString("") { page ->
            val activeClass = if (page == activePage) " eb-platform-nav__item--active" else ""
            val current = if (page == activePage) " aria-current=\"page\"" else ""
            """<a class="eb-platform-nav__item$activeClass" href="${page.path}"$current>${icon(page.iconId, "md")}<span>${page.label}</span></a>"""
        }
        val accountName = session.account?.name?.takeIf { it.isNotBlank() }?.escapeHtml() ?: "플랫폼 관리자"
        renderPage(
            "eb-platform-shell",
            """
                <aside class="eb-platform-sidebar">
                    <a class="eb-platform-sidebar__brand" href="/app/admin">${icon("icon-bridge-logo", "lg")}<span>EduBridge</span></a>
                    <p class="eb-platform-sidebar__label">플랫폼 관리</p>
                    <nav class="eb-platform-nav" aria-label="플랫폼 관리자 메뉴">$navigation</nav>
                    <a class="eb-platform-sidebar__help" href="/support">${icon("icon-help", "sm")}도움말</a>
                </aside>
                <div class="eb-platform-main">
                    <header class="eb-platform-topbar">
                        <p>플랫폼 관리자</p>
                        <div class="eb-platform-topbar__actions">
                            <span class="eb-platform-account">$accountName</span>
                            <button class="eb-account-menu" id="platform-logout-button">${icon("icon-logout", "sm")}<span>로그아웃</span></button>
                        </div>
                    </header>
                    <main class="eb-platform-content" id="content" tabindex="-1">$contentHtml</main>
                </div>
            """.trimIndent(),
        )
        wireLogout()
    }

    private fun wireLogout() {
        (document.getElementById("platform-logout-button") as? HTMLButtonElement)?.onclick = {
            scope.launch { accountRepository.logout() }
            null
        }
    }
}

/** `/app/admin`의 플랫폼 현황 화면. */
object PlatformDashboardPage {
    private val scope = MainScope()
    private val repository = SharedGraph.createRepository()

    fun open(session: SessionState) {
        renderLoading(session)
        scope.launch {
            val result = repository.pendingAcademyApplicationsResult()
            // "0건"과 "못 읽음"이 같은 타일이면 관리자는 승인 대기가 없다고 믿는다(#30).
            PlatformDashboardShell.render(
                session,
                PlatformDashboardShell.Page.DASHBOARD,
                content(if (result.isSuccess) result.items.size else null),
            )
        }
    }

    private fun renderLoading(session: SessionState) {
        PlatformDashboardShell.render(
            session,
            PlatformDashboardShell.Page.DASHBOARD,
            """
                <div class="eb-page-header"><div><h1 class="eb-page-title">대시보드</h1><p class="eb-meta">플랫폼 운영 현황을 불러오는 중이에요.</p></div></div>
                <section class="eb-card eb-platform-loading" aria-live="polite">${icon("icon-refresh", "md", "eb-spin")}<span>승인 요청 현황을 불러오는 중이에요.</span></section>
            """.trimIndent(),
        )
    }

    /** [pendingCount]가 null이면 **조회 실패**다 — 0건과 구분해서 그린다(#30). */
    private fun content(pendingCount: Int?): String {
        val value = if (pendingCount != null) {
            """<strong class="eb-platform-overview__value">$pendingCount<span>건</span></strong>"""
        } else {
            """<strong class="eb-platform-overview__value">—</strong>"""
        }
        val caption = if (pendingCount != null) {
            "신청 내용을 검토한 뒤 승인해 주세요."
        } else {
            "대기 건수를 불러오지 못했어요. 목록에서 직접 확인해 주세요."
        }
        return """
            <div class="eb-page-header">
                <div><h1 class="eb-page-title">대시보드</h1><p class="eb-meta">EduBridge 플랫폼 운영 현황이에요.</p></div>
            </div>
            <section class="eb-platform-overview" aria-label="승인 요청 현황">
                <article class="eb-card eb-platform-overview__card">
                    <span class="eb-platform-overview__label">대기 중인 학원 승인</span>
                    $value
                    <p class="eb-meta">${caption.escapeHtml()}</p>
                    <a class="eb-button eb-button--primary" href="/app/admin/applications">학원 승인 관리</a>
                </article>
            </section>
        """.trimIndent()
    }
}
